"use client";

import { Show, SignInButton, UserButton } from "@clerk/nextjs";
import classNames from "classnames";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { SubmitEvent, Suspense, useCallback, useEffect, useRef, useState } from "react";

function UserArea() {
	return (
		<div>
			<Show when="signed-in">
				<UserButton
					showName={true}
					appearance={{
						elements: {
							userButtonBox: {
								color: "white",
							},
						},
					}}
				/>
			</Show>
			<Show when="signed-out">
				<SignInButton>
					<span className="cursor-pointer">🔑 Sign in</span>
				</SignInButton>
			</Show>
		</div>
	);
}

function SearchBar() {
	const params = useSearchParams();
	const filters = params.getAll("filters");
	const [searchPrompt, setSearchPrompt] = useState(filters.join(" "));
	const router = useRouter();

	function submit(e: SubmitEvent<HTMLFormElement>) {
		e.preventDefault();
		router.push(
			`/recipes?${new URLSearchParams({
				filters: searchPrompt,
			})}`,
		);
	}

	return (
		<form onSubmit={submit}>
			<input
				className="bg-white text-black rounded-l p-1 px-2 inline-block border-l border-y w-40"
				type="text"
				placeholder="Search recipes"
				value={searchPrompt}
				onChange={(e) => {
					setSearchPrompt(e.target.value);
				}}
			/>
			<button
				aria-label="search button"
				className="bg-blue-300 border-blue-800 border-r border-y hover:bg-blue-200 rounded-r p-1 px-2"
			>
				🔎
			</button>
		</form>
	);
}

function Hamburger({ toggled, setToggled }: { toggled: boolean; setToggled: (t: boolean) => void }) {
	const onClick = useCallback(() => {
		setToggled(!toggled);
	}, [setToggled, toggled]);

	return (
		<>
			<div
				data-testid="hamburger"
				onClick={onClick}
				className={classNames({
					"w-4 h-4 text-black p-4": true,
					"hamburger-toggle": toggled,
				})}
			>
				<div
					className={classNames({
						"-translate-x-2 w-4 h-0.5 bg-white rounded transition-all absolute duration-150": true,
						"-translate-y-1.5": !toggled,
						"rotate-45 translate-y-0": toggled,
					})}
				></div>
				<div
					className={classNames({
						"-translate-x-2 w-4 bg-white rounded transition-all absolute duration-150": true,
						"h-0.5": !toggled,
						"h-0": toggled,
					})}
				></div>
				<div
					className={classNames({
						"-translate-x-2 w-4 h-0.5 bg-white rounded transition-all absolute duration-150": true,
						"translate-y-1.5": !toggled,
						"-rotate-45 translate-y-0": toggled,
					})}
				></div>
			</div>
		</>
	);
}

export default function Nav() {
	const [toggled, setToggled] = useState(false);
	const [hamsized, setHamsized] = useState(true);
	const ref = useRef<HTMLDivElement | null>(null);

	const calcHamsized = useCallback(() => {
		setHamsized((ref.current?.clientWidth || 0) < 840);
	}, []);

	const updateRef = useCallback(
		(newRef: HTMLDivElement) => {
			ref.current = newRef;
			calcHamsized();
		},
		[calcHamsized],
	);

	useEffect(() => {
		window.addEventListener("resize", calcHamsized);

		return () => {
			window.removeEventListener("resize", calcHamsized);
		};
	}, [calcHamsized]);

	return (
		<div
			ref={updateRef}
			className="transition-all duration-150 text-white bg-blue-600"
			role="navigation"
			aria-label="main navigation"
		>
			<div className="w-full flex flex-row gap-4 p-2 items-center">
				<Link href="/" className="nav-link">
					⏲️ Our Cookbook
				</Link>
				{!hamsized ? (
					<>
						<Link href="/recipes/" className="nav-link">
							🍳 Recipes
						</Link>
						<Link href="/menus/" className="nav-link">
							📜 Menus
						</Link>
						<div className="grow"></div>
						<UserArea />
						<Suspense>
							<SearchBar />
						</Suspense>
					</>
				) : (
					<>
						<div className="grow"></div>
						<Hamburger toggled={toggled} setToggled={setToggled} />
					</>
				)}
			</div>

			<div
				className={classNames({
					"transition-all origin-top duration-150 flex flex-col gap-1 text-right": true,
					"p-2 scale-y-100 opacity-100": hamsized && toggled,
					"p-0 scale-y-0   oapacity-0": !(hamsized && toggled),
				})}
			>
				{hamsized && toggled && (
					<>
						<Link href="/recipes/" className="nav-link">
							🍳 Recipes
						</Link>
						<Link href="/menus/" className="nav-link">
							📜 Menus
						</Link>
						<UserArea />
						<Suspense>
							<SearchBar />
						</Suspense>
					</>
				)}
			</div>
		</div>
	);
}
